import { RegistryItemFragment } from '@graphql/generated';
import React from 'react';
import { Box, ButtonV2, CheckboxV2, DrawerV2, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Info } from '@withjoy/joykit/icons';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useAutoDismissUpdateConfirmation } from './useAutoDismissUpdateConfirmation';
import { useUpdateForm } from './useUpdateForm';
import { useDrawerVisibilty } from './useDrawerVisibilty';
import { RegistryItemDisplayDetails } from './components/RegistryItemDisplayDetails/RegistryItemDisplayDetails';
import { useTranslation } from '@shared/core';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface RecentlyAddedRegistryItemUpdateDrawerProps {
  item: RegistryItemFragment;
}

const ConfirmationBanner: React.FC = ({ children }) => {
  return (
    <Box background="black" display="flex" alignItems="center" justifyContent="center" color="white" paddingX={6} paddingY={5}>
      <TextV2 typographyVariant="body1">{children}</TextV2>
    </Box>
  );
};

const Root = (props: RecentlyAddedRegistryItemUpdateDrawerProps) => {
  const { item } = props;
  const { showConfirmationBanner, shouldShowConfirmationBanner } = useAutoDismissUpdateConfirmation();
  const { toggleRecentlyAddedRegistryItemUpdateDrawerSetting, saveRecentlyAddedRegistryItemUpdateDrawerSettings } = useCatalogLoggingContext();

  const handleOnUpdateItem = useEventCallback(() => {
    saveRecentlyAddedRegistryItemUpdateDrawerSettings({});
    showConfirmationBanner();
  });

  const { formik, hasChanged, isUpdatingRegistryItem } = useUpdateForm({ itemId: item.id, onUpdate: handleOnUpdateItem });

  const isMobile = useIsMobileScreen();

  const { t } = useTranslation('catalogRegistry');

  const translations = t('recentlyAddedRegistryItemUpdateDrawer');

  const markAsMustHaveFormFieldProps = formik.getFieldProps('markAsMustHave');

  return (
    <>
      {shouldShowConfirmationBanner && <ConfirmationBanner>{translations.updateConfirmationMessage()}</ConfirmationBanner>}
      <DrawerV2.Body height="auto">
        <form onSubmit={formik.handleSubmit}>
          <Flex rowGap={8} flexDirection="column">
            <RegistryItemDisplayDetails item={item} />
            <Flex rowGap={5} flexDirection="column">
              <TextV2 typographyVariant="hed1">{translations.updateSectionHeader()}</TextV2>
              <Flex columnGap={4}>
                <CheckboxV2
                  overrides={{
                    Control: {
                      props: {
                        _checked: {
                          backgroundColor: 'white',
                          borderColor: 'mono14',
                          color: 'mono14'
                        }
                      }
                    }
                  }}
                  {...formik.getFieldProps('markAsMustHave')}
                  {...markAsMustHaveFormFieldProps}
                  onChange={e => {
                    toggleRecentlyAddedRegistryItemUpdateDrawerSetting({
                      name: 'RecentlyAddedRegistryItemUpdateDrawer.markAsMostWanted',
                      value: !markAsMustHaveFormFieldProps.value
                    });
                    markAsMustHaveFormFieldProps.onChange(e);
                  }}
                >
                  {translations.markAsMostWantedToggle.label()}
                </CheckboxV2>

                <TooltipV2
                  placement="top"
                  triggerType={isMobile ? 'click' : 'hover'}
                  content={
                    <TextV2 width={'196px'} textAlign="center">
                      {translations.markAsMostWantedToggle.tooltipMessage()}
                    </TextV2>
                  }
                >
                  <Info size={16} />
                </TooltipV2>
              </Flex>
            </Flex>

            <ButtonV2 type="submit" intent="neutral" disabled={!hasChanged || isUpdatingRegistryItem}>
              {translations.updateCtaMessage()}
            </ButtonV2>
          </Flex>
        </form>
      </DrawerV2.Body>
    </>
  );
};

export const RecentlyAddedRegistryItemUpdateDrawer = () => {
  const isMobile = useIsMobileScreen();

  const { currentItem, isOpen, onClose } = useDrawerVisibilty();
  const { t } = useTranslation('catalogRegistry');

  return (
    <DrawerV2
      isOpen={isOpen && !!currentItem}
      onClose={onClose}
      anchor={isMobile ? 'bottom' : 'right'}
      size={isMobile ? 'auto' : undefined}
      overrides={{
        CloseButton: {
          props: {
            top: '50%',
            transform: 'translateY(-50%)',
            right: 6,
            padding: 0,
            borderRadius: 'full',
            border: '1px solid',
            borderColor: 'mono5',
            height: 8,
            width: 8,
            minWidth: 'unset',
            color: 'black'
          }
        }
      }}
    >
      <DrawerV2.Header borderBottom="1px solid" borderBottomColor={'mono3'} typographyVariant="hed3">
        {t('recentlyAddedRegistryItemUpdateDrawer', 'drawerTitle')()}
        <DrawerV2.CloseButton />
      </DrawerV2.Header>

      {currentItem && <Root item={currentItem} />}
    </DrawerV2>
  );
};
